import { useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { FiPlus, FiTrash2, FiEdit, FiSearch, FiChevronLeft, FiChevronRight, FiTool, FiCreditCard } from 'react-icons/fi';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td,
    Badge, Button, HStack, useToast, Spinner, Center,
    TableContainer,
    Card, CardBody, Input, Select, useDisclosure,
    InputGroup, InputLeftElement, IconButton, Flex, Text, ButtonGroup, Tooltip,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay
} from '@chakra-ui/react';
import StudentModal from '../components/StudentModal';
import StudentScanCodeModal from '../components/StudentScanCodeModal';

import { useAuth } from '../context/AuthContext';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';
import { getGradeLabelOverride } from '../utils/gradeLabels';

const STATUS_MAP = {
    NOT_ARRIVED: { label: '未到校', color: 'gray' },
    ARRIVED: { label: '已到校', color: 'green' },
    HOMEWORK_PENDING: { label: '作業中', color: 'orange' },
    READY_FOR_PICKUP: { label: '可接回', color: 'blue' },
    PARENT_EN_ROUTE: { label: '家長路途中', color: 'purple' },
    PICKUP_COMPLETED: { label: '已接回', color: 'black' },
    LEAVE: { label: '請假', color: 'yellow' }
};

const StudentsPage = () => {
    const { user } = useAuth();
    // [A2] 年級顯示依登入者機構 grade_labels 渲染；沒設定（天籟現況）時 fallback 回現行文字
    const gradeLabels = user?.institution?.grade_labels || null;
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Server-Side Pagination & Filtering State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 40;

    const searchInputRef = useRef(null);
    const [appliedSearch, setAppliedSearch] = useState('');
    const [filterClassId, setFilterClassId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Cleanup filter changes to reset page
    useEffect(() => {
        setCurrentPage(1);
    }, [filterClassId, filterStatus]);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentStudentId, setCurrentStudentId] = useState(null);
    const scanCodeDisclosure = useDisclosure();
    const [selectedStudentForCode, setSelectedStudentForCode] = useState(null);
    const deleteDisclosure = useDisclosure();
    const [studentToDeleteId, setStudentToDeleteId] = useState(null);
    const cancelRef = useRef();

    const handleSearchSubmit = () => {
        setAppliedSearch(searchInputRef.current?.value?.trim() || '');
        setCurrentPage(1);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchSubmit();
        }
    };

    // fetchData now accepts options
    const fetchData = async (options = {}) => {
        try {
            // Only show full spinner if it's the first load or explicit hard reload
            if (!options.silent && students.length === 0) {
                setLoading(true);
            }

            // Calculate skip
            const skip = (currentPage - 1) * itemsPerPage;

            // Prepare params
            const params = {
                skip,
                limit: itemsPerPage,
                ...(filterStatus && { statuses: [filterStatus] }),
                ...(appliedSearch && { search: appliedSearch }),
                ...(filterClassId && { class_id: filterClassId })
            };

            const [studentsRes, classesRes] = await Promise.all([
                authAPI.getStudents(params),
                authAPI.getClassesLite()
            ]);

            setStudents(studentsRes.data);
            setClasses(classesRes.data);

            // Handle Total Count from Header
            const total = parseInt(studentsRes.headers['x-total-count'] || '0', 10);
            setTotalCount(total);

        } catch (error) {
            console.error(error);
            toast({ title: "無法獲取資料", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Trigger Fetch when params change (Keep standard loading behavior for pagination/filtering changes)
    useEffect(() => {
        // When params change, we generally want visual feedback, but maybe not full spinner if data exists.
        // Let's use silent=true if we already have students, to make it smoother.
        fetchData({ silent: students.length > 0 });
    }, [currentPage, appliedSearch, filterClassId, filterStatus]);

    // Local processing state for specific items (e.g. delete button loading)
    const [processingId, setProcessingId] = useState(null);

    const handleStatusUpdate = async (studentId, newStatus) => {
        // 1. Snapshot for rollback
        const originalStudents = [...students];

        // 2. Optimistic Update
        setStudents(prev => prev.map(student =>
            student.id === studentId
                ? { ...student, status: newStatus }
                : student
        ));

        try {
            // 3. API Call
            await authAPI.updateStudentStatus(studentId, newStatus);
            // Success: Do nothing (UI is already updated)
        } catch (error) {
            // 4. Rollback on error
            setStudents(originalStudents);
            toast({ title: "狀態更新失敗", description: "已還原狀態", status: "error" });
        }
    };

    const handleDelete = (id) => {
        setStudentToDeleteId(id);
        deleteDisclosure.onOpen();
    };

    const confirmDelete = async () => {
        const id = studentToDeleteId;
        deleteDisclosure.onClose();
        setStudentToDeleteId(null);

        setProcessingId(id);
        try {
            await authAPI.deleteStudent(id);
            setStudents(prev => prev.filter(s => s.id !== id));
            setTotalCount(prev => prev - 1);
            toast({ title: "刪除成功", status: "success" });
        } catch (error) {
            const detail = error?.response?.data?.detail || error?.message || '未知錯誤';
            toast({ title: "刪除失敗", description: detail, status: "error", duration: 8000, isClosable: true });
            console.error('Delete student error:', error?.response?.data || error);
        } finally {
            setProcessingId(null);
        }
    };

    const handleCreate = () => {
        setIsEditMode(false);
        setCurrentStudentId(null);
        onOpen();
    };

    const handleEdit = (student) => {
        setIsEditMode(true);
        setCurrentStudentId(student.id);
        onOpen();
    };

    const handleManageScanCode = (student) => {
        setSelectedStudentForCode(student);
        scanCodeDisclosure.onOpen();
    };

    // [Phase 3] Global Fix Tool
    const handleGlobalFix = async () => {
        if (!window.confirm("確定要執行「一鍵修復家長連結」嗎？\n\n系統將掃描所有資料，自動合併重複的手機號碼並清理空帳號。\n建議在閒置時段執行。")) return;

        try {
            const res = await authAPI.scanParents();
            alert(`✅ 修復完成！\n\n檢查家長數：${res.data.active_parents_scanned}\n處理異常數：${res.data.parents_with_duplicates}\n🔗 合併連結數：${res.data.merged_links}\n🗑️ 清理空帳號：${res.data.deleted_placeholders}`);

            // Refresh data
            fetchData({ silent: true });
        } catch (err) {
            console.error(err);
            toast({ title: "執行失敗", description: "請稍後再試或聯繫管理員", status: "error" });
        }
    };

    // Calculate Pages
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    const paginatedStudents = students; // Server already paginated

    // Filter classes for dropdown based on role
    const displayedClasses = user?.role === 'teacher'
        ? filterClassesForStaffUser(classes, user)
        : classes;

    return (
        <Flex direction="column" h="100%" w="100%" minW="0" overflow="hidden">
            <Box flexShrink={0} mb={4}>
                <Flex justifyContent="space-between" alignItems="center" mb={6}>
                    <Heading size="lg" color="brand.800">學生管理</Heading>
                    <HStack>
                        <Button leftIcon={<FiTool />} variant="outline" colorScheme="gray" onClick={handleGlobalFix}>
                            一鍵修復連結
                        </Button>
                        <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={handleCreate}>
                            新增學生
                        </Button>
                    </HStack>
                </Flex>

                <HStack spacing={4}>
                    <InputGroup maxW="300px">
                        <InputLeftElement pointerEvents="none">
                            <FiSearch color="gray.300" />
                        </InputLeftElement>
                        <Input
                            ref={searchInputRef}
                            placeholder="搜尋學生姓名..."
                            defaultValue=""
                            onKeyDown={handleSearchKeyDown}
                            bg="white"
                        />
                    </InputGroup>

                    <Button leftIcon={<FiSearch />} colorScheme="brand" onClick={handleSearchSubmit}>
                        搜尋
                    </Button>

                    <Select
                        placeholder="所有班級"
                        maxW="200px"
                        bg="white"
                        value={filterClassId}
                        onChange={(e) => setFilterClassId(e.target.value)}
                    >
                        {displayedClasses.map(cls => (
                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                    </Select>

                    <Select
                        placeholder="所有狀態"
                        maxW="200px"
                        bg="white"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {Object.entries(STATUS_MAP).map(([key, config]) => (
                            <option key={key} value={key}>{config.label}</option>
                        ))}
                    </Select>
                </HStack>
            </Box>

            {/* Scrollable Table Container */}
            <Card w="100%" flex="1" overflow="hidden">
                <CardBody p={0} position="relative" display="flex" flexDirection="column" h="100%">

                    {/* Table Wrapper: Absolute positioning to force bounds match parent, not content */}
                    <Box flex="1" overflow="hidden" position="relative">
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            overflow="auto"
                            sx={{ overscrollBehavior: 'none' }}
                        >
                            <Table variant="simple" minW="800px" whiteSpace="nowrap" bg="white">
                                <Thead bg="gray.50" position="sticky" top={0} zIndex={1} shadow="sm">
                                    <Tr>
                                        <Th whiteSpace="nowrap">姓名</Th>
                                        <Th whiteSpace="nowrap">年級</Th>
                                        <Th whiteSpace="nowrap">班級</Th>
                                        <Th whiteSpace="nowrap">目前狀態</Th>
                                        <Th whiteSpace="nowrap">狀態更改</Th>
                                        <Th whiteSpace="nowrap">操作</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {loading ? (
                                        <Tr>
                                            <Td colSpan={5}>
                                                <Center py={10}>
                                                    <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.500" size="xl" />
                                                </Center>
                                            </Td>
                                        </Tr>
                                    ) : (
                                        <>
                                            {paginatedStudents.map((student) => (
                                                <Tr key={student.id}>
                                                    <Td fontWeight="bold" whiteSpace="nowrap">{student.full_name}</Td>
                                                    <Td whiteSpace="nowrap">
                                                        <Badge colorScheme="purple" variant="subtle">
                                                            {student.grade ? (getGradeLabelOverride(student.grade, gradeLabels) || `${student.grade}年級`) : '未設定'}
                                                        </Badge>
                                                    </Td>
                                                    <Td whiteSpace="nowrap">
                                                        {(() => {
                                                            const classes = student.classes || (student.class ? [student.class] : []);
                                                            if (classes.length === 0) return '未分班';

                                                            // Priority: AnChin > English > Others
                                                            const anChin = classes.find(c => (c.department === 'AfterSchool' || c.name.includes('安親')));
                                                            if (anChin) return anChin.name;

                                                            const english = classes.find(c => (c.department === 'English' || c.name.includes('英文')));
                                                            if (english) return english.name;

                                                            return classes[0].name;
                                                        })()}
                                                    </Td>
                                                    <Td whiteSpace="nowrap">
                                                        <Badge colorScheme={STATUS_MAP[student.status]?.color || 'gray'}>
                                                            {STATUS_MAP[student.status]?.label || student.status}
                                                        </Badge>
                                                    </Td>
                                                    <Td whiteSpace="nowrap">
                                                        <Select
                                                            size="sm"
                                                            value={student.status}
                                                            onChange={(e) => handleStatusUpdate(student.id, e.target.value)}
                                                            w="130px"
                                                            bg="white"
                                                            borderColor={STATUS_MAP[student.status]?.color || "gray.200"}
                                                        >
                                                            {Object.entries(STATUS_MAP).map(([key, config]) => (
                                                                <option key={key} value={key}>
                                                                    {config.label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </Td>
                                                    <Td whiteSpace="nowrap">
                                                        <HStack spacing={2}>
                                                            <Tooltip label="編輯學生" placement="top" hasArrow>
                                                                <IconButton
                                                                    icon={<FiEdit />}
                                                                    size="sm"
                                                                    colorScheme="teal"
                                                                    variant="solid"
                                                                    onClick={() => handleEdit(student)}
                                                                    aria-label="編輯"
                                                                />
                                                            </Tooltip>
                                                            <Tooltip label="管理學生條碼" placement="top" hasArrow>
                                                                <IconButton
                                                                    icon={<FiCreditCard />}
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                    variant="outline"
                                                                    onClick={() => handleManageScanCode(student)}
                                                                    aria-label="管理學生條碼"
                                                                />
                                                            </Tooltip>
                                                            {['receptionist', 'admin', 'super_admin'].includes(user?.role) && (
                                                                <Tooltip label="刪除學生" placement="top" hasArrow>
                                                                    <IconButton
                                                                        icon={<FiTrash2 />}
                                                                        size="sm"
                                                                        colorScheme="red"
                                                                        variant="ghost"
                                                                        onClick={() => handleDelete(student.id)}
                                                                        isLoading={processingId === student.id}
                                                                        aria-label="刪除"
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                            {students.length === 0 && (
                                                <Tr>
                                                    <Td colSpan={5} textAlign="center" py={8} color="neutral.500">
                                                        尚無學生資料
                                                    </Td>
                                                </Tr>
                                            )}
                                        </>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>

                    {/* Pagination Controls */}
                    {
                        totalCount > 0 && (
                            <Flex p={4} justify="space-between" align="center" borderTop="1px solid" borderColor="gray.100">
                                <Text fontSize="sm" color="gray.600">
                                    顯示第 {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} 筆，共 {totalCount} 筆
                                </Text>
                                <ButtonGroup size="sm" isAttached variant="outline">
                                    <Button
                                        leftIcon={<FiChevronLeft />}
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        isDisabled={currentPage === 1}
                                    >
                                        上一頁
                                    </Button>
                                    <Button isDisabled _disabled={{ color: 'black', cursor: 'default', bg: 'gray.50' }}>
                                        {currentPage} / {totalPages}
                                    </Button>
                                    <Button
                                        rightIcon={<FiChevronRight />}
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        isDisabled={currentPage === totalPages}
                                    >
                                        下一頁
                                    </Button>
                                </ButtonGroup>
                            </Flex>
                        )
                    }
                </CardBody>
            </Card>

            <StudentModal
                isOpen={isOpen}
                onClose={onClose}
                isEditMode={isEditMode}
                studentId={currentStudentId}
                onSuccess={(studentData) => {
                    if (studentData) {
                        // Local Update (Optimistic-like, but actually confirmed data)
                        if (isEditMode) {
                            setStudents(prev => prev.map(s => s.id === studentData.id ? studentData : s));
                        } else {
                            // Check if creating new student fits current filters
                            // If we want to be simple, just prepend it. Filtering might hide it if it doesn't match,
                            // but usually user wants to see what they created.
                            // However, if we have server-side pagination, prepending might make list > itemsPerPage.
                            // That's acceptable for UX.
                            setStudents(prev => [studentData, ...prev]);
                            setTotalCount(prev => prev + 1);
                        }
                    } else {
                        // Fallback if no data returned (shouldn't happen with new modal)
                        fetchData({ silent: true });
                    }
                }}
                classes={classes}
            />

            <StudentScanCodeModal
                isOpen={scanCodeDisclosure.isOpen}
                onClose={() => {
                    scanCodeDisclosure.onClose();
                    setSelectedStudentForCode(null);
                }}
                student={selectedStudentForCode}
            />

            <AlertDialog
                isOpen={deleteDisclosure.isOpen}
                leastDestructiveRef={cancelRef}
                onClose={deleteDisclosure.onClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.600">
                            ⚠️ 刪除學生
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text fontWeight="bold" mb={2}>此動作會完全移除學生的資料！請再三確認！</Text>
                            <Text color="gray.600" fontSize="sm">刪除後無法復原，包含所有出勤紀錄、家長連結與掃描條碼都將一併移除。</Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={deleteDisclosure.onClose}>
                                取消
                            </Button>
                            <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                                確認刪除
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex >
    );
};

export default StudentsPage;
